import { useState, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { toast } from "sonner";
import { useTranslation } from "@/packages/utils";
import { SettingsSection } from "./SettingsSection";
import {
  OrgContextForm,
  isCompleteOrgContext,
} from "@/components/workspace/OrgContextForm";
import type { OrgContextAnswers } from "@/components/workspace/OrgContextForm";

interface OrgContextSettingsSectionProps {
  workspaceId: Id<"workspaces">;
  orgContext?: OrgContextAnswers;
  isReadOnly: boolean;
}

export function OrgContextSettingsSection({
  workspaceId,
  orgContext,
  isReadOnly,
}: OrgContextSettingsSectionProps) {
  const { t } = useTranslation("settings");
  const updateOrgContext = useMutation(
    api.workspaces.updateWorkspaceOrgContext,
  );
  const [answers, setAnswers] = useState<Partial<OrgContextAnswers>>(
    orgContext ?? {},
  );

  useEffect(() => {
    setAnswers(orgContext ?? {});
  }, [orgContext]);

  const handleChange = useCallback(
    async (newAnswers: Partial<OrgContextAnswers>) => {
      setAnswers(newAnswers);
      if (isReadOnly || !isCompleteOrgContext(newAnswers)) return;
      try {
        await updateOrgContext({ workspaceId, orgContext: newAnswers });
        toast.success(t("workspace.orgContext.saved"));
      } catch {
        toast.error(t("workspace.orgContext.saveFailed"));
      }
    },
    [isReadOnly, updateOrgContext, workspaceId, t],
  );

  return (
    <SettingsSection
      title={t("workspace.orgContext.title")}
      description={t("workspace.orgContext.description")}
    >
      <div className="max-w-md">
        <OrgContextForm
          answers={answers}
          onChange={handleChange}
          disabled={isReadOnly}
        />
      </div>
    </SettingsSection>
  );
}
